// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { normalizeParams } from './genericHelpers';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the given marketplaceRootURL parameter or the same domain and port as the frontend
  return marketplaceRootURL ? marketplaceRootURL.replace(/\/$/, '') : `${window.location.origin}`;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // If headers are not set, we assume that the body should be serialized as transit format.
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body;
  const bodyMaybe = shouldSerializeBody ? { body: serialize(body) } : {};

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    headers: headers || { 'Content-Type': 'application/transit+json' },
    ...bodyMaybe,
    ...rest,
  };

  return window.fetch(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

// Keep the previous parameter order for the get method.
// For now, only GET has own specific function, but you can create more or use request directly.
const get = path => {
  const requestOptions = {
    method: methods.GET,
  };

  return request(path, requestOptions);
};
// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};
export const initiateWalletPrivileged = body => {
  return post('/api/initiate-wallet-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

export const createListingWithIntegration = body => {
  return post('/api/create-listing', body);
};

export const SharetribeApi = Object.freeze({
  /**
   * Check if a username is available in the system.
   */
  checkUsernameAvailability: body =>
    get(`/api/check-username-availability?username=${body.username}`),

  updateUserProfile: body => post('/api/update-user-profile', body),
  updateUserWallet: body => post('/api/update-user-wallet', body),
  updateOwnAndOtherUserWallet: body => post('/api/update-wallet-details', body),

  /**
   * Update a listing in Sharetribe.
   */
  updateListing: body => post('/api/listing/update', body),

  /**
   * Updates the country value of a listing in Sharetribe.
   * @param {Object} body - The data to send for updating the country value of a listing.
   * @returns {Promise} - A promise that resolves to the result of the update operation.
   */
  updateCountryInListing: body => post('/api/listing/update-country', body),

  createTxNotificationApi: body => post('/api/create-tx-notification', body),
  createMessageNotificationApi: body => post('/api/create-msg-notification', body),
  markSeenNotification: body => post('/api/mark-seen-notification', body),

  deleteNotification: ({ txId, notifId, userId }) =>
    post('/api/delete-notification', { txId, notifId, userId }),

  fetchUserShippingAddress: body => get(`/api/fetch-user-shipping-address?id=${body.userId}`),

  /**
   * Checks if a user has a Stripe connected account.
   * @param {Object} body - The data to send for checking the user's Stripe connection.
   * @returns {Promise<any>} - A promise that resolves to the result of the check operation.
   */
  checkUserStripeConnection: body => get(`/api/fetch-user-stripe-connection?id=${body.userId}`),
  /**
   * Updates the metadata of a transaction in Sharetribe.
   * @param {Object} body - The data to send for updating the transaction metadata.
   * @returns {Promise} - A promise that resolves to the result of the update operation.
   */
  updateTransactionMetadata: body => post('/api/update-transaction-metadata', body),
});

/**
 * An immutable object containing API methods for interacting with MongoDB-related endpoints.
 *
 * @namespace MongoDBApi
 *
 * @property {function(): Promise<any>} getAllFilters - Fetches all available filters from the backend.
 */

// export const MongoDBApi = Object.freeze({
//   getAllFilters: () => get('/api/get-all-filters'),
// });
/**
 * An immutable object containing API methods for interacting with Shopify-related endpoints.
 *
 * @namespace ShopifyApi
 */

export const ShopifyApi = Object.freeze({
  connectShopifyCustomAppApi: body => {
    return post('/api/shopify/connect-custom-app', body);
  },

  /**
   * Fetches the Shopify OAuth authorization URL for a given shop and user ID.
   * @param {Object} body - The data to send for fetching the authorization URL.
   * @returns {Promise<string>} - A promise that resolves to the authorization URL.
   */
  fetchShopifyAuthUrlApi: ({ shop, userId }) => {
    const { shop: normalizedShop, userId: normalizedUserId } = normalizeParams(shop, userId);
    return get(
      `/api/shopify/auth-url?${new URLSearchParams({
        shop: normalizedShop,
        userId: normalizedUserId,
      }).toString()}`
    );
  },

  /**
   * Fetches the products for a given shop and user ID.
   * @param {Object} body - The data to send for fetching the products.
   * @returns {Promise<any>} - A promise that resolves to the result of the product fetch.
   */
  fetchShopifyProductsApi: ({ shop, userId }) => {
    const { shop: normalizedShop, userId: normalizedUserId } = normalizeParams(shop, userId);
    return get(
      `/api/shopify/products?${new URLSearchParams({
        shop: normalizedShop,
        userId: normalizedUserId,
      }).toString()}`
    );
  },

  startShopifyProductSyncApi: ({ shop, userId, importFootballOnly }) => {
    const { shop: normalizedShop, userId: normalizedUserId } = normalizeParams(shop, userId);
    return post('/api/shopify/products/sync', {
      shop: normalizedShop,
      userId: normalizedUserId,
      importFootballOnly,
    });
  },

  syncShopifyListingApi: ({ listingId, userId }) => {
    return post('/api/shopify/listing/sync', {
      listingId,
      userId,
    });
  },

  fetchShopifyProductSyncStatusApi: ({ shop, userId }) => {
    const { shop: normalizedShop, userId: normalizedUserId } = normalizeParams(shop, userId);
    return get(
      `/api/shopify/products/sync-status?${new URLSearchParams({
        shop: normalizedShop,
        userId: normalizedUserId,
      }).toString()}`
    );
  },

  /**
   * Updates a Shopify product using the given data.
   * @param {Object} body - The data to send for updating the product.
   * @returns {Promise} - A promise that resolves to the result of the product update.
   */
  updateShopifyProductApi: body => {
    return post('/api/shopify/update', body);
  },
  /**
   * Handles disconnecting a Shopify store for a given user.
   * @param {Object} body - The data to send for disconnecting the store.
   * @returns {Promise} - A promise that resolves to the disconnect result.
   */
  handleShopifyUninstallApi: body => {
    /**
     * @param {string} body.shop - The shop to disconnect.
     * @param {string} body.userId - The user ID associated with the disconnect.
     */
    return post('/api/shopify/uninstall', body);
  },
});

export const ShippoApi = {
  /**
   * Create a shipping address using Shippo.
   * @param {Object} body - The data to send for creating the address.
   * @returns {Promise} - A promise that resolves to the result of the address creation.
   */
  createAddress: body => post('/api/shippo/create-address', body),

  /**
   * Get available carriers from Shippo.
   * @param {Object} body - The data to send for fetching carriers.
   * @returns {Promise} - A promise that resolves to the list of carriers.
   */
  getCarriers: body => post('/api/shippo/get-carriers', body),

  /**
   * Create a shipping label using Shippo.
   * @param {Object} body - The data to send for creating the label.
   * @returns {Promise} - A promise that resolves to the result of the label creation.
   */
  createLabel: body => post('/api/shippo/create-label', body),

  /**
   * Create a return shipping label using Shippo.
   * @param {Object} body - The data to send for creating the return label.
   * @returns {Promise} - A promise that resolves to the result of the return label creation.
   */
  createReturnLabel: body => post('/api/shippo/create-return-label', body),

  /**
   * Get tracking status directly from Shippo using carrier + tracking number.
   * @param {Object} body
   * @param {string} body.trackingNumber
   * @param {string} body.carrier  // e.g. "ups", "usps", "dhl_express", etc
   */
  getTrackingByNumber: body => post('/api/shippo/get-tracking-by-number', body),

  /**
   * Get tracking status for a shipment using Shippo.
   * @param {Object} body - The data containing the transaction ID.
   * @returns {Promise} - A promise that resolves to the tracking status of the shipment.
   */
  getTrackingStatus: body => get(`/api/shippo/get-tracking?transactionId=${body.transactionId}`),
};

// PayPal
const paypalOnboardingLink = body => {
  return post('/api/paypal/onboarding-links', body);
};

const paypalMerchantOnboardingStatus = () => {
  return get('/api/paypal/merchant-signup-status');
};

const paypalCreateOrder = body => {
  return post('/api/paypal/create-order', body);
};

const paypalCaptureOrder = body => {
  return post('/api/paypal/capture-order', body);
};

const paypalCompleteOrder = body => {
  return post('/api/paypal/complete-order', body);
};

const initiatePrivilegedWithPaypal = body => {
  return post('/api/paypal/initiate-privileged', body);
};

const transitionPrivilegedWithPaypal = body => {
  return post('/api/paypal/transition-privileged', body);
};

const initiateSpecWithPaypal = body => {
  return post('/api/paypal/initiate-spec', body);
};

const getOrderIdWithPaypal = body => {
  return post('/api/paypal/get-order-id', body);
};

export const PayPalApi = Object.freeze({
  getOnboardingLink: paypalOnboardingLink,
  getMerchantOnboardingStatus: paypalMerchantOnboardingStatus,
  createOrder: paypalCreateOrder,
  captureOrder: paypalCaptureOrder,
  completeOrder: paypalCompleteOrder,
  initiatePrivileged: initiatePrivilegedWithPaypal,
  transitionPrivileged: transitionPrivilegedWithPaypal,
  initiateSpeculative: initiateSpecWithPaypal,
  getOrderId: getOrderIdWithPaypal,
});

/**
 * An immutable object containing API methods for interacting with Stripe-related endpoints.
 *
 * @namespace StripeApi
 */
export const StripeApi = Object.freeze({
  /**
   * Create a Stripe payment intent.
   * @param {Object} body - The data to send for creating the payment intent.
   * @returns {Promise} - A promise that resolves to the result of the payment intent creation.
   */
  createStripePaymentIntent: body => post('/api/stripe/create-payment-intent', body),

  /**
   * Capture a Stripe payment intent.
   * @param {Object} body - The data to send for capturing the payment intent.
   * @returns {Promise} - A promise that resolves to the result of the payment intent capture.
   */
  captureStripePaymentIntent: body => post('/api/stripe/capture-payment-intent', body),

  refundStripePaymentIntent: body => post('/api/stripe/refund-payment-intent', body),

  createPayout: body => post('/api/stripe/create-payout', body),
  createExpressCheckoutPaymentIntent: body =>
    post('/api/stripe/create-express-checkout-payment-intent', body),
});
